// #region Imports
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import {
  getErrorLoadingIndividualBookmarks,
  getIndividualBookmarks,
  getIsLoadingIndividualBookmarks,
  populateIndividualBookmarksForUser
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import BookmarkedIndividual from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual";
import {
  getIndividualsByIds,
  populateIndividualsByIds
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import {
  createMRTColumnHelper,
  type MRT_TableOptions
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
// #endregion

// #region Component Input Props
export interface BookmarkedIndividualsProps {
  selectedMemberUserIds?: Guid[];
  onSelected: (individual: Individual) => void;
};

const columnHelper = createMRTColumnHelper<BookmarkedIndividual>();

export function BookmarkedIndividuals(props: Readonly<BookmarkedIndividualsProps>) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  // Bookmark Selectors
  const bookmarks = useSelector(getIndividualBookmarks());
  const bookmarksLoading = useSelector(getIsLoadingIndividualBookmarks());
  const errorLoadingBookmarks = useSelector(getErrorLoadingIndividualBookmarks());

  // All individualIds to fetch
  const bookmarkedIndividualIds = useMemo(() => {
    return bookmarks?.map(b => b.individualId).filter((id): id is Guid => !!id) ?? []
  }, [bookmarks]);

  // Individual Selectors
  const individuals = useSelector(getIndividualsByIds(bookmarkedIndividualIds));
  const individualEntriesJson = useSelector((s: RootState) => s.individuals.byId.entries);
  const isLoadingById = useSelector((s: RootState) => s.individuals.byId.loading);
  const errorById = useSelector((s: RootState) => s.individuals.byId.error);

  const individualsById = useMemo(() => {
    const map: Record<string, Individual> = {};
    for (const ind of individuals) {
      map[ind.id.value] = ind;
    }
    return map;
  }, [individuals]);

  // #region Lifecycle Hooks
  useEffect(() => {
    if (!bookmarkedIndividualIds.length) return;

    const missingIds = bookmarkedIndividualIds.filter((id) => {
      if (individualEntriesJson[id.value]) return false;
      if (isLoadingById[id.value]) return false;
      return true;
    });

    if (!missingIds.length) return;

    dispatch(populateIndividualsByIds({ session, ids: missingIds }));
  }, [
    dispatch,
    session,
    bookmarkedIndividualIds,
    individualEntriesJson,
    isLoadingById
  ]);

  useEffect(() => {
    if ((bookmarks?.length ?? 0) > 0) return;
    dispatch(populateIndividualBookmarksForUser({ session }));
  }, [bookmarks?.length, dispatch, session]);

  useEffect(() => {
    if (errorLoadingBookmarks) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [errorLoadingBookmarks]);

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const isLoading = !!isLoadingById[id.value];
    const hasError = !!errorById[id.value];
    const loadingText = isLoading ? "Loading..." : "Unknown Individual";

    const name =
      individual?.fullName ??
      (hasError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={individual}
          session={session}
        />
        <RowTitle>{name}</RowTitle>
      </NameCellContainer>
    );
  }, [individualsById, isLoadingById, errorById, session]);

  const renderCategoriesColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const isLoading = !!isLoadingById[id.value];
    const hasError = !!errorById[id.value];
    const loadingText = isLoading ? "Loading..." : "None";
    const label = hasError ? "Error loading" : loadingText;

    if (!individual?.profile?.categories) {
      return <Chip disabled label={label} />;
    }

    return individual.profile.categories.map((category) => (
      <Chip key={category} label={category} />
    ));
  }, [individualsById, isLoadingById, errorById]);

  const renderDescriptionColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const isLoading = !!isLoadingById[id.value];
    const hasError = !!errorById[id.value];
    const loadingText = isLoading ? "Loading..." : "Description unavailable";
    const text = hasError ? "Error loading" : loadingText;


    return individual?.profile.description ?? text;
  }, [individualsById, isLoadingById, errorById]);

  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderCategoriesColumn, {
      header: "Categories",
      id: "categories",
    }),
    columnHelper.accessor(renderDescriptionColumn, {
      header: "Description",
      id: "description",
    }),
  ], [renderNameColumn, renderCategoriesColumn, renderDescriptionColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedIndividual> = {
    columns,
    data: bookmarks ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const individual = individualsById[row.original.individualId.value];
        if (!individual) throw new Error("Individual not found for bookmark");
        onSelected(individual);
      },
      sx: {
        pointerEvents: selectedMemberUserIds?.some(id =>
          row.original?.individualId && id.isEqualTo(row.original.individualId)
        ) ? "none" : "auto",
        cursor: "pointer"
      },
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: errorLoadingBookmarks === null
      ? undefined
      : {
        color: "error",
        children: "Error loading data",
      },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length ?? 0,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading,
      // pagination,
      showAlertBanner: errorLoadingBookmarks !== null
        || bookmarkedIndividualIds.some(individualId => !!errorById[individualId.value]),
      showProgressBars: bookmarksLoading,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedIndividual> options={tableOptions} />;
}
